<template>
  <div class="customer-content">
    <div class="customer-details-container">
      <div class="customer-details-container-header">
        {{ titleText }}
        <AuditInformation 
          v-if="!loading && customer && customer.id > 0 && customer.createdByUser && customer.createdDate && customer.lastModifiedByUser && customer.lastModifiedDate"
          :created-by-user="customer.createdByUser"
          :created-date="customer.createdDate"
          :last-modified-by-user="customer.lastModifiedByUser"
          :last-modified-date="customer.lastModifiedDate"
        />
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading && customer"
        class="customer-details-container-content"
      >
        <LockingButton
          class="save-button"
          :disabled="!isDirty"
          :locked="saving"
          :text="'Save'"
          @click="onSaveCustomerClicked"
        />
        <div class="customer-details-flex-container-side-by-side">
          <TextInput
            v-model="customer.firstName"
            :show-required="true"
            label="First Name"
            class="first-item"
          />
          <TextInput
            v-model="customer.lastName"
            label="Last Name"
            class="second-item"
          />
        </div>
        <div class="customer-details-flex-container-side-by-side">
          <TextInput
            v-model="customer.email"
            label="Email"
            class="first-item"
          />
          <PhoneNumberInput
            v-model="customer.phoneNumber"
            class="second-item"
          />
        </div>
        <div class="customer-details-flex-container-side-by-side">
          <TextInput
            v-model="customer.addressLine1"
            label="Address Line 1"
            class="first-item"
          />
          <TextInput
            v-model="customer.addressLine2"
            label="Address Line 2"
            class="second-item"
          />
        </div>
        <div class="customer-details-flex-container-side-by-side">
          <TextInput
            v-model="customer.city"
            label="City"
            class="first-item"
          />
          <div class="second-item state-and-postal-code">
            <Dropdown
              v-model="customer.stateId"
              label="State"
              :options="stateDropdownOptions"
              class="first-item"
            />
            <TextInput
              v-model="customer.postalCode"
              label="Postal Code"
              class="second-item"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="notes-container">
      <div class="notes-container-header">
        <div class="notes-container-header-text">
          Notes
        </div>
        <a
          v-show="!loading && customer && customer.id > 0"
          class="create-note"
          @click="onCreateNoteClicked"
        >
          Create
        </a>
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading && customer"
      >
        <NoteTile
          v-for="note in customer.notes"
          :key="note.id"
          :note="note"
          @delete="onDeleteNote"
          @edit="onEditNote"
        />
      </div>
      <div
        v-if="(!customer || customer.id === 0) && !loading"
        class="message"
      >
        * Notes can be added to the customer once it is initally created.
      </div>
    </div>
    <transition name="slide-up">
      <NoteModal 
        v-if="showNoteModal && noteToEdit"
        :note="noteToEdit"
        :customer-id="id"
        @note-changed="getCustomer"
        @close="showNoteModal = false"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import NoteModal from '@/components/modals/NoteModal.vue'
import Toaster from '@meforma/vue-toaster'
import NoteTile from '@/components/elements/NoteTile.vue'
import { CustomerService } from '@/services/customer.service'
import { NoteService } from '@/services/note.service'
import { CustomerDetails } from '@/@types/responses/customer/CustomerDetails'
import { Note } from '@/@types/responses/note/Note'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import PhoneNumberInput from '@/components/shared/PhoneNumberInput.vue'
import { CustomerMessage } from '@/@types/messages/CustomerMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import Dropdown from '@/components/shared/Dropdown.vue'
import { StateService } from '@/services/states.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'Customer',
  components: {
    AuditInformation,
    Dropdown,
    LoadingPulse,
    LockingButton,
    NoteModal,
    NoteTile,
    PhoneNumberInput,
    TextInput
  },
  beforeRouteLeave() {
    if (this.showNoteModal) {
      this.showNoteModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const customerService = inject(PROVIDER_CONSTANTS.customerService) as CustomerService
    const noteService = inject(PROVIDER_CONSTANTS.noteService) as NoteService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      customerService,
      noteService,
      stateService,
      toast
    }
  },
  data() {
    let noteToEdit: Note|undefined
    let customer: CustomerDetails|undefined
    let unchangedCustomer: CustomerDetails|undefined

    return {
      loading: true,
      customer,
      unchangedCustomer,
      saving: false,
      showNoteModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      noteToEdit
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.customer
      const b = this.unchangedCustomer

      return (a?.firstName !== b?.firstName ||
              a?.lastName !== b?.lastName ||
              a?.email !== b?.email ||
              a?.phoneNumber !== b?.phoneNumber ||
              a?.addressLine1 !== b?.addressLine1 ||
              a?.addressLine2 !== b?.addressLine2 ||
              a?.city !== b?.city ||
              a?.stateId !== b?.stateId ||
              a?.postalCode !== b?.postalCode) && 
              a?.firstName.trim() !== ''
    },
    titleText(): string {
      return !this.customer || this.customer.id === 0 ? 'Create Customer' : 'Edit Customer'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    this.getStates().then(() => {
      if (this.id > 0) {
        this.getCustomer()
      } else {
        this.customer = new CustomerDetails(0)
        this.unchangedCustomer = new CustomerDetails(0)
        this.loading = false
      }
    })
  },
  methods: {
    getCustomer(): Promise<void> {
      return this.customerService.getCustomer(this.customer ? this.customer.id : this.id).then(customer => {
        this.customer = customer
        this.unchangedCustomer = JSON.parse(JSON.stringify(customer))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the customer. Try reloading the page.')
        }
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onCreateNoteClicked(): void {
      this.noteToEdit = new Note(0)
      this.showNoteModal = true
    },
    onDeleteNote(note: Note): void {
      this.noteService.deleteNote(note.id).then(() => {
        this.getCustomer()
        this.toast.success('The note was deleted successfully.')
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the note. Please try again.')
        }
      })
    },
    onEditNote(note: Note): void {
      this.noteToEdit = note
      this.showNoteModal = true
    },
    onSaveCustomerClicked(): void {
      if (!this.customer) return

      this.saving = true

      const customerMessage = new CustomerMessage(this.customer.firstName, this.customer.lastName, this.customer.phoneNumber, this.customer.email, this.customer.addressLine1, this.customer.addressLine2, this.customer.city, this.customer.stateId, this.customer.postalCode)
      if (this.customer.id === 0)
        this.customerService.createCustomer(customerMessage).then(objectCreatedResponse => {
          if (!this.customer) return

          this.customer.id = objectCreatedResponse.id
          this.getCustomer().then(() => {
            this.$router.replace(`/customer/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The customer was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the customer. Please try again.')
          }
        })
      else
        this.customerService.updateCustomer(this.id, customerMessage).then(() => {
          this.saving = false
          this.toast.success('The customer was updated successfully.')
          this.$router.push('/customers')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the customer. Please try again.')
          }
        })
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.customer)
        document.title = `${this.customer.firstName} ${this.customer.lastName} - Short Action Customs, LLC`
    }
  }
})
</script>

<style scoped lang="scss">
.customer-content {
  display: flex;
  flex-direction: column;
  padding: 15px;

  @include desktop {
    flex-direction: row;
  }

  > .customer-details-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;
    position: relative;

    @include desktop {
      flex: 1 1 70%;
      height: max-content;
      padding: 15px;
      background-color: $Color-fill-gray;
      border-radius: 2px;
      @include element-shadow;
    }

    > .customer-details-container-header {
      line-height: 37px;
      font-size: 24px;
      font-weight: bold;
      padding-bottom: 10px;
      border-bottom: 1px solid $Color-border-gray;
    }

    > .loading-spinner {
      margin: auto;
      padding: 20px;
    }

    > .customer-details-container-content {
      padding-top: 15px;

      > .save-button {
        position: absolute;
        top: 0;
        right: 0;

        @include desktop {
          top: 15px;
          right: 15px;
        }
      }

      > .customer-details-flex-container-side-by-side {
        display: flex;
        flex-direction: column;

        @include desktop {
          flex-direction: row;
        }

        > .first-item {
          margin-bottom: 15px;
          flex: 1;

          @include desktop {
            flex: 0 0 calc(50% - 10px);
            margin-right: 10px;
          }
        }

        > .second-item {
          margin-bottom: 15px;
          flex: 1;

          @include desktop {
            flex: 0 0 50%;
          }

          &.state-and-postal-code {
            display: flex;

            > .first-item {
              flex: 0 0 calc(50% - 10px);
              margin-right: 10px;
            }

            > .second-item {
              flex: 0 0 50%;
            }
          }
        }
      }
    }
  }

  > .notes-container {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: 15px;

    @include desktop {
      flex: 1 1 30%;
      padding-left: 20px;
    }

    > .notes-container-header {
      display: flex;
      margin-bottom: 15px;

      > .notes-container-header-text {
        margin: 0 auto 0 0;
        font-size: 24px;
        font-weight: bold;
      }

      > .create-note {
        @include flat-button;
        margin: auto 0;
      }
    }

    > .loading-spinner {
      margin: auto;
    }

    > .message {
      font-style: italic;
      color: $Color-font-medium;
    }
  }

  > .slide-up {
    @include modal-animation;
  }
}
</style>
